import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { LiliTransaction, TransactionExternalAccountType } from '../../types/lili-transaction';
import uploadIcon from '../../svg/upload.svg';
import viewUpload from '../../svg/view-upload.svg';
import Heading from '../../../../generic-components/Heading';
import Button from '../../../../generic-components/Button';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import { ReceiptPreviewPopup } from '../shared/ReceiptPreviewPopup';
import { useStore } from '../../../../store';
import { useUploadReceiptMutation } from '../../../../hooks/mutations/useUploadReceiptMutation';
import Spinner, { SpinnerColor } from '../../../../generic-components/Spinner';
import { useDownloadReceiptMutation } from '../../../../hooks/mutations/useDownloadReceiptMutation';
import { flattenServerErrorResponse } from '../../../../helpers/errors.helper';
import { IServerErrorResponse } from '../../../../types';
import { featureAccess, LimitedFeatureType } from '../../../../services/appAccess.service';
import { FeatureAccess } from '../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

interface TransactionReceiptPhotoProps {
  transaction: LiliTransaction;
}

export const TransactionReceiptPhoto = ({ transaction }: TransactionReceiptPhotoProps) => {
  const { t } = useTranslation();
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData | null>(null);
  const elInput = useRef<HTMLInputElement>(null);
  const {
    user: { customerId },
  } = useStore((state) => state);
  const updateTransactionReceipt = useStore((state) => state.updateTransactionReceipt);
  const { accountExternalId } = useStore((state) => state.getPersonalAccount());
  const { selectedSyncedCard } = useStore((state) => state);

  const filePathId = transaction.categoryDetails?.receiptPath || '';

  const {
    mutate: AsyncDownloadAsset,
    isLoading,
    data,
  } = useDownloadReceiptMutation({
    customerId,
    transactionId: transaction.transactionId,
    isExternal: transaction?.externalAccountType === TransactionExternalAccountType.PLAID,
    accountExternalId,
    externalAccountId: selectedSyncedCard?.externalId,
  });

  const {
    mutate: AsyncUploadReceipt,
    isLoading: isUploadingAsset,
    error,
  } = useUploadReceiptMutation({
    customerId,
    transactionId: transaction.transactionId,
    formData,
    isExternal: transaction?.externalAccountType === TransactionExternalAccountType.PLAID,
    accountExternalId,
    externalAccountId: selectedSyncedCard?.externalId,
    onSuccess: (urlId) => {
      updateTransactionReceipt(urlId);
      AsyncDownloadAsset();
    },
  });
  const transactionsEditPermission = featureAccess(null, LimitedFeatureType.TransactionsAddReceiptAndNote);

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e?.target?.files?.[0]) return;
    const file = e.target.files[0];
    const fd = new FormData();
    fd.append('file', file, `Receipt-${transaction.transactionId}`);
    setFormData(fd);
  };

  const handleAction = () => {
    elInput?.current?.click();
  };

  useEffect(() => {
    if (!formData) return;
    AsyncUploadReceipt();
  }, [formData]);

  useEffect(() => {
    if (!filePathId) return;
    AsyncDownloadAsset();
  }, [filePathId]);

  useEffect(() => {
    if (error) {
      if (elInput.current) {
        elInput.current.value = '';
      }
      const { localizedMessage, message } = flattenServerErrorResponse(error as IServerErrorResponse);
      toast.error(localizedMessage || message || 'Please try again.', {
        position: 'bottom-center',
        autoClose: 3500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
      });
    }
  }, [error]);

  return (
    <>
      {isPreview && data?.url && (
        <UserMessage onClick={() => setIsPreview(false)} id='previewWrapper'>
          <ReceiptPreviewPopup
            data={data}
            onFormDataChange={(fd) => setFormData(fd)}
            isLoading={isLoading || isUploadingAsset}
            onClose={() => setIsPreview(false)}
            transaction={transaction}
          />
        </UserMessage>
      )}
      <div className='flex justify-between items-start bg-bw02 rounded-[6px] px-[21px] py-[16px] text-bw06 font-metricR text-[16px]'>
        <div className='flex items-center gap-[6px]'>
          <Heading title={filePathId ? t('VIEW_RECEIPT') : t('ATTACH_A_RECEIPT')} className='' />
          {filePathId && (
            <Button
              testId='viewPhoto'
              onClick={() => setIsPreview(true)}
              className=''
              id='viewReceipt'
              childElement={<img src={viewUpload} className='cursor-pointer' alt='view upload' />}
            />
          )}
        </div>
        <input
          disabled={transactionsEditPermission !== FeatureAccess.READ_WRITE}
          ref={elInput}
          hidden
          accept='image/jpeg,image/png,.pdf'
          id='replaceImage'
          type='file'
          placeholder='Replace Image'
          onChange={handleFileInput}
        />
        {isUploadingAsset || isLoading ? (
          <Spinner containerClassName='ml-auto w-fit' spinnerColor={SpinnerColor.Blue} height='1rem' width='1rem' />
        ) : (
          <Button
            disable={transactionsEditPermission !== FeatureAccess.READ_WRITE}
            onClick={handleAction}
            className=''
            id=''
            childElement={
              transactionsEditPermission !== FeatureAccess.READ_WRITE ? null : (
                <img src={uploadIcon} className='cursor-pointer' alt='upload icon' />
              )
            }
          />
        )}
      </div>
    </>
  );
};
